import { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@store/auth.store';

/**
 * The admin panel's one React Query client — main.tsx hands it to <QueryClientProvider>.
 *
 * B-523: the cache belongs to ONE account. Everything in it was fetched with the token of whoever
 * was logged in: their profile (`['auth', 'profile']`, which useProfileSync merges into the store),
 * dashboard layout, sessions and login history, notifications, lists with personal data. The store's
 * logout() is reached from several places (useLogout, IdleLogout, PolicyGate, the 401 handler in
 * api.ts) and only useLogout used to clear the cache — so after an idle, 401 or policy logout the
 * next person logging in in the same tab inherited the previous account's cache. The profile was
 * still fresh (staleTime 10 min), nothing refetched, and the panel showed the previous person's
 * name, role and menu on top of the new person's tokens (the API kept enforcing the real ones).
 *
 * Hence one rule, kept here instead of at every call site: whenever the logged-in account changes —
 * logout (→ null) or a login as someone else — the whole cache goes. A token refresh or a profile
 * update of the same account (same id) keeps it. The listener runs synchronously inside the store
 * update, so nothing renders the new state with the old account's data.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

useAuthStore.subscribe((state, previous) => {
  if ((state.user?.id ?? null) !== (previous.user?.id ?? null)) queryClient.clear();
});
